import { Button, Modal } from "@omnitok/ui";
import { AlertTriangle, CheckCircle2 } from "lucide-react";
import { Spinner } from "@pervasivemind/bi-components";
import { Disclosure } from "@pervasivemind/admin-ui";
import { useState } from "react";

import { useT } from "@/i18n";
import type { AiHomeBrokenView, AiHomeViewsCheck } from "@/types";

/**
 * The saved AI Home views (portal) a change would break, before it goes
 * live (2026-10-26): Publish, and a save of a published report. A warning,
 * not a block: the person sees the list (view, owner, what fails) and may
 * go ahead. A check that could not run says so with the portal's words;
 * it is never shown as "nothing breaks".
 */
export function BrokenViewsWarning({
  found,
  checking,
  live,
}: {
  found: AiHomeViewsCheck | null;
  checking: boolean;
  /** A save of a published report (its changes go live) vs a publish. */
  live: boolean;
}): JSX.Element | null {
  const { t } = useT();
  const [copied, setCopied] = useState(false);
  if (checking) {
    return (
      <div data-testid="views-checking" className="flex items-center gap-2 rounded-md bg-neutral-50 px-3 py-2 text-xs text-neutral-700">
        <Spinner size={14} ariaLabel={t("views.checking")} />
        {t("views.checking")}
      </div>
    );
  }
  if (found === null) return null;
  if (found.check_error !== undefined) {
    return (
      <div data-testid="views-check-failed" className="flex items-start gap-2 rounded-md border border-warning/40 bg-warning/10 px-3 py-2 text-xs text-warning-dark">
        <AlertTriangle size={14} className="mt-0.5 shrink-0" aria-hidden="true" />
        <span>
          <strong>{t("views.checkFailed")}</strong> {t("views.checkFailedBody", { error: found.check_error })}
        </span>
      </div>
    );
  }
  const already = found.already_broken.length > 0 && (
    <Disclosure
      className="border-neutral-200 bg-neutral-50"
      summary={t("views.alreadyBroken", { count: found.already_broken.length })}
    >
      <ViewsTable views={found.already_broken} />
    </Disclosure>
  );
  if (found.broken.length === 0) {
    return (
      <>
        <div data-testid="views-ok" className="flex items-start gap-2 rounded-md border border-success/40 bg-success/10 px-3 py-2 text-xs text-success-dark">
          <CheckCircle2 size={14} className="mt-0.5 shrink-0" aria-hidden="true" />
          <span>{found.checked === 0 ? t("views.none") : t("views.ok", { count: found.checked ?? 0 })}</span>
        </div>
        {already}
      </>
    );
  }
  const stillWork = (found.checked ?? 0) - found.broken.length - found.already_broken.length;
  const copy = async (): Promise<void> => {
    const lines = found.broken.map((v) => `${v.view_name}\t${v.owner_name} <${v.owner_email}>\t${whatFails(v)}`);
    await navigator.clipboard.writeText(lines.join("\n"));
    setCopied(true);
  };
  return (
    <>
      <div data-testid="views-broken" className="flex flex-col gap-2 rounded-md border border-warning/40 bg-warning/10 px-3 py-2 text-xs text-neutral-800">
        <div className="flex items-start gap-2">
          <AlertTriangle size={16} className="mt-0.5 shrink-0 text-warning-dark" aria-hidden="true" />
          <div>
            <p className="text-sm font-semibold text-neutral-900">{t("views.brokenTitle", { count: found.broken.length })}</p>
            <p>{t(live ? "views.brokenBodyLive" : "views.brokenBody", { count: found.checked ?? 0 })}</p>
          </div>
        </div>
        <ViewsTable views={found.broken} />
        <p className="text-neutral-700">
          {stillWork > 0 && (
            <>
              {t("views.stillWork", { count: stillWork })}
              {" · "}
            </>
          )}
          <button type="button" onClick={() => void copy()} className="font-semibold text-brand-700 hover:underline">
            {copied ? t("views.copied") : t("views.copy")}
          </button>
        </p>
      </div>
      {already}
    </>
  );
}

/** The server's 409 about saved views, shown before anything goes live:
 *  the person goes back or goes ahead (the same request, confirmed). */
export function BrokenViewsDialog({
  conflict,
  title,
  body,
  keepLabel,
  goLabel,
  busy,
  live,
  onKeep,
  onGo,
}: {
  conflict: AiHomeViewsCheck;
  title: string;
  body: string;
  keepLabel: string;
  goLabel: string;
  busy: boolean;
  live: boolean;
  onKeep: () => void;
  onGo: () => void;
}): JSX.Element {
  return (
    <Modal
      open
      onClose={onKeep}
      title={title}
      size="lg"
      footer={
        <>
          <Button ghost onClick={onKeep} disabled={busy} data-testid="views-keep">
            {keepLabel}
          </Button>
          <Button variant="primary" onClick={onGo} loading={busy} data-testid="views-go">
            {goLabel}
          </Button>
        </>
      }
    >
      <div className="space-y-3 text-sm text-neutral-700">
        <p>{body}</p>
        <BrokenViewsWarning found={conflict} checking={false} live={live} />
      </div>
    </Modal>
  );
}

/** What fails in a view, one entry per distinct error with the elements
 *  that share it: five elements reading the same missing time window are
 *  one line naming the five, not the same error five times (staging
 *  2026-10-26). The whole view's error has no titles. */
export function failureGroups(view: AiHomeBrokenView): { titles: string[]; error: string }[] {
  if (view.error !== null) return [{ titles: [], error: view.error }];
  const byError = new Map<string, string[]>();
  for (const e of view.elements) {
    const titles = byError.get(e.error);
    if (titles === undefined) byError.set(e.error, [e.title]);
    else titles.push(e.title);
  }
  return [...byError].map(([error, titles]) => ({ titles, error }));
}

function whatFails(view: AiHomeBrokenView): string {
  return failureGroups(view)
    .map((g) => (g.titles.length > 0 ? `${g.titles.join(", ")}: ${g.error}` : g.error))
    .join("; ");
}

function ViewsTable({ views }: { views: AiHomeBrokenView[] }): JSX.Element {
  const { t } = useT();
  // A group's publish names the report each view breaks on.
  const several = new Set(views.map((v) => v.report_key)).size > 1;
  return (
    <table className="w-full border-collapse text-left text-xs">
      <thead>
        <tr className="text-neutral-600">
          <th scope="col" className="px-2 py-1 font-semibold">{t("views.colView")}</th>
          <th scope="col" className="px-2 py-1 font-semibold">{t("views.colOwner")}</th>
          <th scope="col" className="px-2 py-1 font-semibold">{t("views.colFails")}</th>
        </tr>
      </thead>
      <tbody>
        {views.map((v) => (
          <tr key={`${v.report_key ?? ""}:${v.view_id}`} className="border-t border-warning/20 align-top">
            <td className="px-2 py-1.5 font-semibold text-neutral-900">
              {v.view_name}
              {several && v.report_key !== undefined && (
                <>
                  <br />
                  <span className="font-mono text-xs font-normal text-neutral-600">{v.report_key}</span>
                </>
              )}
            </td>
            <td className="px-2 py-1.5">
              {v.owner_name}
              <br />
              <span className="text-neutral-600">{v.owner_email}</span>
            </td>
            <td className="px-2 py-1.5">
              <span className="mr-1 rounded-full bg-warning/20 px-1.5 py-0.5 font-semibold text-warning-dark">
                {v.error !== null ? t("views.wholeView") : t("views.someElements", { count: v.elements.length })}
              </span>
              {failureGroups(v).map((g) => (
                <span key={g.error} className="mt-1 block break-words">
                  {g.titles.length > 0 && <span className="font-semibold text-neutral-900">{g.titles.join(", ")}: </span>}
                  {g.error}
                </span>
              ))}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
